import { useEffect, useRef, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import {
  RESOLVE_EXPERIMENT_VARIANT,
  type ResolveExperimentVariantResponse,
} from '@scio3/graphql-client';
import { useAuthStore } from '@/stores/auth-store';
import { readForcedVariant } from '../utils/force-variant';

/**
 * The general experiment-consume seam — the single way any surface asks
 * "which arm is this company in?" for a server-decided OptiGrowth experiment.
 *
 * Backend-first + sticky, decider-backed (NOT PostHog):
 *  - Arm already on the company (`experiments[key]`) → return it synchronously.
 *    Every consumer reads the one stored value, so they agree and it never flips.
 *  - Otherwise call the open-api `resolveExperimentVariant` decider ONCE — it
 *    decides server-side on companyId, persists write-once, and returns the arm
 *    (or `null` = not enrolled: ineligible / held out). We optimistically patch the
 *    in-store company so the rest of the session reads the same value without a refetch.
 *
 * Returns `{ variant, isLoading }`:
 *  - `variant` — the arm string, or `null` when not enrolled (caller renders the default).
 *  - `isLoading` — true only until the first resolve settles; a consumer that renders
 *    per-arm UI should hold while loading to avoid a flash.
 *
 * Adding an experiment = a backend registry entry + a render branch on `variant`.
 * No new plumbing here.
 *
 * The one-shot resolve is an imperative side-effecting mutation (an action, not
 * declarative data-fetching), so `useEffect` is the right tool.
 */
export function useExperimentVariant(key: string): {
  variant: string | null;
  isLoading: boolean;
} {
  const storedVariant = useAuthStore((s) => s.company?.experiments?.[key] ?? null);
  const hasCompany = useAuthStore((s) => !!s.company);
  const userId = useAuthStore((s) => s.me?._id);
  const setCompany = useAuthStore((s) => s.setCompany);

  // QA-only force override (non-prod, `?force_variant_<key>=<arm>`). Read once on
  // mount; `null` for normal users → falls through to the decider below.
  const [forced] = useState<string | null>(() => readForcedVariant(key));

  const [state, setState] = useState<{ variant: string | null; settled: boolean }>(() =>
    storedVariant != null
      ? { variant: storedVariant, settled: true }
      : { variant: null, settled: false },
  );
  const firedRef = useRef(false);

  const [resolveExperimentVariant] = useMutation<ResolveExperimentVariantResponse>(
    RESOLVE_EXPERIMENT_VARIANT,
  );

  useEffect(() => {
    // QA override forces the arm — skip the decider entirely.
    if (forced != null) return;
    // Sticky: an already-assigned arm short-circuits everything (no decider call).
    if (storedVariant != null) {
      setState({ variant: storedVariant, settled: true });
      return;
    }
    // Wait for authentication; resolve at most once per mount.
    if (!hasCompany || !userId || firedRef.current) return;
    firedRef.current = true;

    let cancelled = false;
    void (async () => {
      try {
        const res = await resolveExperimentVariant({ variables: { key } });
        const arm = res.data?.resolveExperimentVariant ?? null;
        if (cancelled) return;
        setState({ variant: arm, settled: true });
        // Patch the in-store company so other consumers read the same arm now.
        if (arm != null) {
          const current = useAuthStore.getState().company;
          if (current && !current.experiments?.[key]) {
            setCompany({
              ...current,
              experiments: { ...(current.experiments ?? {}), [key]: arm },
            });
          }
        }
      } catch (error) {
        // Best-effort: the decider also returns null on its own errors, and a failed
        // resolve must never block the UI — settle as not-enrolled.
        if (cancelled) return;
        console.warn(`[experiment] resolve "${key}" failed`, error);
        setState({ variant: null, settled: true });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [key, forced, storedVariant, hasCompany, userId, setCompany, resolveExperimentVariant]);

  // QA override wins, returned synchronously — the decider is never consulted.
  if (forced != null) return { variant: forced, isLoading: false };
  return { variant: state.variant, isLoading: !state.settled };
}
